import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS, DURATION_FORMATS, updateMe } from '@mkcore/platform/schema';
import { Button, Card, Field, NativeSelect, PageHeader } from '@mkcore/ui';
import { meQuery, useMe } from '@/lib/session';
import { useActionMutation } from '@/lib/mutation';
import { setFormatPreferences } from '@/lib/format';
import { setTheme, useTheme, type ThemePref } from '@/lib/theme';
import { setLanguage } from '@/i18n';
import { TokensPanel } from '@/features/admin/tokens-panel';
import { ConnectionsPanel } from '@/features/admin/connections-panel';
import { NotificationSettings } from '@/features/collab/notifications';
import { OAuthClientsPanel } from '@/features/admin/oauth-clients-panel';
import { ChatIdentitiesPanel } from '@/features/admin/chat-identities-panel';
import { WorkSettingsPanel } from '@/features/engagements/work-settings-panel';
import { PlacesPanel } from '@/features/time/places-panel';
import { RecurringPanel } from '@/features/time/recurring-panel';

export const Route = createFileRoute('/_app/settings')({ component: Settings });

const zones = ['Europe/Amsterdam', 'Europe/London', 'Europe/Berlin', 'UTC', 'America/New_York', 'Asia/Singapore'];

function Settings() {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const { pref } = useTheme();
  const [locale, setLocale] = useState(me.locale);
  const [timezone, setTimezone] = useState(me.timezone);
  const [dateFormat, setDateFormat] = useState(me.dateFormat);
  const [durationFormat, setDurationFormat] = useState(me.durationFormat);
  const [weekStart, setWeekStart] = useState(me.weekStart);
  const save = useActionMutation(updateMe, {
    invalidate: [],
    success: t('common.saved'),
    onSuccess: (m) => {
      setLanguage(m.locale);
      setFormatPreferences(m);
      qc.setQueryData(meQuery.queryKey, m);
    },
  });
  return (
    <>
      <PageHeader title={t('settings.title')} />
      <div className="grid gap-8">
        <Card className="grid gap-4 p-6">
          <h2 className="text-lg font-bold">{t('settings.profile')}</h2>
          <form
            className="grid max-w-md gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ input: { locale, timezone, dateFormat, durationFormat, weekStart } });
            }}
          >
            <Field label={t('settings.language')}>
              {(p) => (
                <NativeSelect {...p} value={locale} onChange={(e) => setLocale(e.target.value as 'nl' | 'en')}>
                  <option value="nl">Nederlands</option>
                  <option value="en">English</option>
                </NativeSelect>
              )}
            </Field>
            <Field label={t('settings.timezone')}>
              {(p) => (
                <NativeSelect {...p} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                  {[...new Set([timezone, ...zones])].map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('settings.dateFormat')} hint={t('settings.dateFormatHint')}>
              {(p) => (
                <NativeSelect {...p} value={dateFormat} onChange={(e) => setDateFormat(e.target.value as typeof dateFormat)}>
                  {DATE_FORMATS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('settings.durationFormat')}>
              {(p) => (
                <NativeSelect {...p} value={durationFormat} onChange={(e) => setDurationFormat(e.target.value as typeof durationFormat)}>
                  {DURATION_FORMATS.map((f) => (
                    <option key={f} value={f}>
                      {t(`settings.duration.${f}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('settings.weekStart')}>
              {(p) => (
                <NativeSelect {...p} value={String(weekStart)} onChange={(e) => setWeekStart(Number(e.target.value))}>
                  {[1, 6, 7].map((d) => (
                    <option key={d} value={d}>
                      {t(`settings.weekday.${d}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('settings.theme')}>
              {(p) => (
                <NativeSelect {...p} value={pref} onChange={(e) => setTheme(e.target.value as ThemePref)}>
                  <option value="system">{t('settings.themeSystem')}</option>
                  <option value="light">{t('settings.themeLight')}</option>
                  <option value="dark">{t('settings.themeDark')}</option>
                </NativeSelect>
              )}
            </Field>
            <div>
              <Button type="submit" loading={save.isPending}>
                {t('common.save')}
              </Button>
            </div>
          </form>
        </Card>
        <Card className="p-6">
          <RecurringPanel />
        </Card>
        <Card className="p-6">
          <PlacesPanel />
        </Card>
        <Card className="p-6">
          <h2 className="mb-1 text-lg font-bold">{t('notifications.settingsTitle')}</h2>
          <p className="mb-3 text-sm text-fg-2">{t('notifications.settingsHint')}</p>
          <NotificationSettings />
        </Card>
        <Card className="p-6">
          <ConnectionsPanel />
        </Card>
        <Card className="p-6">
          <TokensPanel />
        </Card>
        <Card className="p-6">
          <OAuthClientsPanel />
        </Card>
        <Card className="p-6">
          <ChatIdentitiesPanel />
        </Card>
        <Card className="p-6">
          <WorkSettingsPanel />
        </Card>
      </div>
    </>
  );
}
